import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { memberService } from '../services/member.service';
import { newcomerService } from '../services/newcomer.service';
import type { RegisteredMember } from '../types/member';
import type { Newcomer } from '../types/newcomer';
import { FaHome, FaSync, FaClock, FaBell, FaChartLine, FaUsers, FaChurch, FaBookOpen, FaUserFriends, FaHeartbeat, FaMoneyBillWave, FaCalendarAlt, FaChartBar, FaTrophy, FaLightbulb, FaExclamationTriangle, FaCheckCircle, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { PastoralCalendar } from '../components/calendar/PastoralCalendar';
import { StatsCardGrid, DiscipleshipManagementSection, DetailedStatsSection, RealTimeActivityMonitor } from '../components/dashboard/StatsCardGrid';
import { RegisteredMembersSection as RegisteredMembersSectionNew } from '../components/dashboard/RegisteredMembersSection';
import { OverallStatusSection } from '../components/dashboard/OverallStatusSection';
import { NewcomerStatusSection } from '../components/dashboard/NewcomerStatusSection';
import { DiscipleshipStatusSection } from '../components/dashboard/DiscipleshipStatusSection';
import { VisitationStatusSection } from '../components/dashboard/VisitationStatusSection';
import { FinanceStatusSection } from '../components/dashboard/FinanceStatusSection';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useNotification } from '../contexts/NotificationContext';

// 성능 최적화를 위한 메모이제이션된 컴포넌트
const MemoizedStatsCardGrid = React.memo(StatsCardGrid);
const MemoizedNewcomerStatusSection = React.memo(NewcomerStatusSection);
const MemoizedDiscipleshipStatusSection = React.memo(DiscipleshipStatusSection);
const MemoizedVisitationStatusSection = React.memo(VisitationStatusSection);
const MemoizedFinanceStatusSection = React.memo(FinanceStatusSection);
const MemoizedDiscipleshipManagementSection = React.memo(DiscipleshipManagementSection);
const MemoizedRegisteredMembersSection = React.memo(RegisteredMembersSectionNew);
const MemoizedOverallStatusSection = React.memo(OverallStatusSection);
// Mock 재정 데이터 - 새로운 재정 시스템 연동 전까지 사용
const mockFinanceData = {
  totalIncome: 2500000,
  totalExpense: 1800000,
  netIncome: 700000,
  profitMargin: 28,
  income: {
    tithe: 1500000,
    thanksgiving: 600000,
    missionary: 200000,
    other: 200000
  },
  expense: {
    ministry: 800000,
    utility: 500000,
    vehicle: 300000,
    other: 200000
  }
};

export const HomePage: React.FC = () => {
  const [registeredMembers, setRegisteredMembers] = useState<RegisteredMember[]>([]);
  const [newcomers, setNewcomers] = useState<Newcomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 실시간 데이터 업데이트를 위한 상태
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');


  // 알림 상태
  const [unreadNotifications, setUnreadNotifications] = useState(3);
  const [showNotifications, setShowNotifications] = useState(false);

  // 실시간 활동 상태
  const [recentActivities, setRecentActivities] = useState([
    { id: 1, type: 'member', user: '김교인', action: '새로운 가족 등록', time: '2분 전', status: 'success' },
    { id: 2, type: 'finance', user: '이집사', action: '주간 헌금 등록', time: '5분 전', status: 'success' },
    { id: 3, type: 'discipleship', user: '목사님', action: '양육 과정 완료', time: '8분 전', status: 'success' },
    { id: 4, type: 'visitation', user: '권사님', action: '방문 기록 작성', time: '12분 전', status: 'success' },
    { id: 5, type: 'event', user: '김전도사', action: '이벤트 생성', time: '15분 전', status: 'success' }
  ]);

  const { addNotification } = useNotification();

  // 모든 useMemo Hook을 컴포넌트 최상단으로 이동
  const totalRegisteredMembers = registeredMembers.length;

  // 성별 데이터 계산 (메모이제이션)
  const genderData = useMemo(() => {
    if (!registeredMembers || registeredMembers.length === 0) return [];
    const counts = registeredMembers.reduce((acc, member) => {
      const gender = member.gender || 'unknown';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(counts).map(([name, value]) => ({
      name: name === 'male' ? '남성' : name === 'female' ? '여성' : '기타/미지정',
      value
    }));
  }, [registeredMembers]);

  // 등급 데이터 계산 (메모이제이션)
  const memberClassData = useMemo(() => {
    if (!registeredMembers || registeredMembers.length === 0) return [];
    const counts = registeredMembers.reduce((acc, member) => {
      const memberClass = member.memberClass || 'unknown';
      acc[memberClass] = (acc[memberClass] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(counts).map(([name, value]) => ({
      name: `등급 ${name.toUpperCase()}`,
      value
    }));
  }, [registeredMembers]);

  // 연령대 데이터 계산 (메모이제이션)
  const ageGroupData = useMemo(() => {
    if (!registeredMembers || registeredMembers.length === 0) return [];
    const getAge = (dateString: string) => {
      const today = new Date();
      const birthDate = new Date(dateString);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    const counts = registeredMembers.reduce((acc, member) => {
      const age = getAge(member.dateOfBirth);
      let group = '기타';
      if (age < 20) group = '10대 이하';
      else if (age < 30) group = '20대';
      else if (age < 40) group = '30대';
      else if (age < 50) group = '40대';
      else if (age < 60) group = '50대';
      else group = '60대 이상';

      acc[group] = (acc[group] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const orderedGroups = ['10대 이하', '20대', '30대', '40대', '50대', '60대 이상', '기타'];
    return orderedGroups
      .map(group => ({ name: group, value: counts[group] || 0 }))
      .filter(item => item.value > 0);
  }, [registeredMembers]);

  // 새가족 통계 계산 (메모이제이션)
  const newcomerStats = useMemo(() => {
    if (!newcomers || !registeredMembers) return { active: 0, graduated: 0, total: 0 };

    const registeredMemberKeys = new Set(
      registeredMembers.map(rm => `${rm.name}-${rm.dateOfBirth}`)
    );

    let graduatedCount = 0;
    newcomers.forEach(nc => {
      if (registeredMemberKeys.has(`${nc.name}-${nc.dateOfBirth}`)) {
        graduatedCount++;
      }
    });
    return {
      active: newcomers.length - graduatedCount,
      graduated: graduatedCount,
      total: newcomers.length,
    };
  }, [newcomers, registeredMembers]);

  // Mock 주간 재정 데이터 - 새로운 재정 시스템 연동 전까지 사용
  const weeklyFinanceData = useMemo(() => {
    return {
      income: Object.entries(mockFinanceData.income).map(([name, value]) => ({ name, value })),
      expense: Object.entries(mockFinanceData.expense).map(([name, value]) => ({ name, value })),
      offeringTrend: [],
      totalIncome: mockFinanceData.totalIncome,
      totalExpense: mockFinanceData.totalExpense,
      netIncome: mockFinanceData.netIncome,
      profitMargin: mockFinanceData.profitMargin
    };
  }, []);

  // 빠른 액션 버튼 데이터
  const quickActions = useMemo(() => [
    {
      id: 'members',
      title: '교인 관리',
      icon: <FaUsers className="h-5 w-5" />,
      description: '등록 교인 정보 관리',
      color: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      link: '/members',
      badge: totalRegisteredMembers
    },
    {
      id: 'newcomers',
      title: '새가족 관리',
      icon: <FaChurch className="h-5 w-5" />,
      description: '새로운 가족들 관리',
      color: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
      link: '/newcomers',
      badge: newcomerStats.active
    },
    {
      id: 'finance',
      title: '재정 관리',
      icon: <FaMoneyBillWave className="h-5 w-5" />,
      description: '주간 재정 현황',
      color: 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700',
      link: '/finance',
      badge: weeklyFinanceData.profitMargin + '%'
    },
    {
      id: 'visitation',
      title: '심방관리',
      icon: <FaBookOpen className="h-5 w-5" />,
      description: '교인 심방 기록 관리',
      color: 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700',
      link: '/visitations',
      badge: '8'
    }
  ], [totalRegisteredMembers, newcomerStats.active, weeklyFinanceData.profitMargin]);

  // 개별 데이터 가져오기 함수들
  const fetchRegisteredMembers = useCallback(async () => {
    try {
      const memberData = await memberService.getAllRegisteredMembers();
      setRegisteredMembers(memberData);
    } catch (err) {
      console.error("Error fetching registered members for dashboard:", err);
      setError('등록 교인 데이터를 불러오는 중 오류가 발생했습니다.');
    }
  }, []);

  const fetchNewcomers = useCallback(async () => {
    try {
      const newcomerData = await newcomerService.getAllNewcomers();
      setNewcomers(newcomerData);
    } catch (err) {
      console.error("Error fetching newcomers for dashboard:", err);
      setError('새가족 데이터를 불러오는 중 오류가 발생했습니다.');
    }
  }, []);


  // 모든 데이터를 한 번에 가져오는 함수
  const fetchAllData = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      await Promise.all([
        fetchRegisteredMembers(),
        fetchNewcomers()
      ]);
      setLastUpdated(new Date());
      addNotification('데이터가 성공적으로 업데이트되었습니다.', 'success');
    } catch (err) {
      console.error("Error fetching all data:", err);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
      addNotification('데이터 업데이트에 실패했습니다.', 'error');
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  }, [fetchRegisteredMembers, fetchNewcomers, addNotification]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 주기적 데이터 업데이트 (5분마다)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAllData();
    }, 5 * 60 * 1000); // 5분마다 업데이트

    return () => clearInterval(interval);
  }, [fetchAllData]);

  // 수동 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 로딩 상태 처리
  if (loading && !isRefreshing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="large" className="min-h-screen" />
      </div>
    );
  }



  return (
    <div className="p-3 md:p-6 lg:p-8 min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* 헤더 섹션 */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
        <div className="flex items-start lg:items-center mb-4 lg:mb-0">
          <FaHome className="text-2xl lg:text-3xl text-gray-600 dark:text-gray-200 mr-3 lg:mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl text-gray-600 dark:text-gray-200 font-bold leading-tight">대시보드</h1>
            <p className="mt-1 text-sm lg:text-base text-gray-500 dark:text-gray-400 leading-relaxed">
              부다페스트한인선교교회 온라인처치의 실시간 현황을 확인하세요
            </p>
          </div>
        </div>

        {/* 컨트롤 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full lg:w-auto">

          {/* 새로고침 버튼 */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`flex items-center space-x-2 px-3 lg:px-4 py-2 rounded-lg shadow-sm transition-all duration-200 ${isRefreshing
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
              }`}
            aria-label="새로고침"
          >
            <FaSync className={`h-3 w-3 lg:h-4 lg:w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-xs lg:text-sm font-medium hidden sm:inline">
              {isRefreshing ? '업데이트 중...' : '새로고침'}
            </span>
          </button>

          {/* 마지막 업데이트 시간 */}
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 p-2 lg:p-3 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <FaClock className="h-3 w-3 lg:h-4 lg:w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <span className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 truncate">
              {lastUpdated.toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })}
            </span>
          </div>

          {/* 알림 버튼 */}
          <button
            className="flex items-center space-x-2 px-3 lg:px-4 py-2 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-all duration-200 relative"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <FaBell className="h-3 w-3 lg:h-4 lg:w-4" />
            <span className="text-xs lg:text-sm font-medium">알림</span>
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadNotifications}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 알림 드롭다운 */}
      {showNotifications && (
        <div className="absolute top-20 right-6 z-50 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">알림</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <div className="flex items-start space-x-3">
                <FaExclamationTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-white">새가족 등록 알림</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">김가족이 새로 등록되었습니다.</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">10분 전</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <div className="flex items-start space-x-3">
                <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-white">양육 완료</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">이가족의 양육 과정이 완료되었습니다.</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">1시간 전</p>
                </div>
              </div>
            </div>
            <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <div className="flex items-start space-x-3">
                <FaMoneyBillWave className="h-5 w-5 text-blue-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-white">주간 헌금 완료</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">주간 헌금 목표 달성을 축하합니다!</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">2시간 전</p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <button className="w-full text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
              모든 알림 보기
            </button>
          </div>
        </div>
      )}

      {/* 오류 메시지 표시 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <FaBell className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">데이터 로딩 오류</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}


      {/* 빠른 액션 섹션 */}
      <div className="mb-8">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">빠른 액션</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <a
              key={action.id}
              href={action.link}
              className={`group flex flex-col sm:flex-row items-center sm:items-start p-4 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 ${action.color} text-white relative overflow-hidden`}
            >
              {/* 배경 패턴 */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-white/20 transform rotate-45"></div>
              </div>

              <div className="flex-shrink-0 relative z-10 mb-2 sm:mb-0 text-center sm:text-left">
                {action.icon}
              </div>
              <div className="sm:ml-3 relative z-10 text-center sm:text-left">
                <h3 className="text-sm font-bold group-hover:underline">{action.title}</h3>
                <p className="text-xs opacity-90 mt-1 hidden sm:block">{action.description}</p>
              </div>

              {/* 배지 */}
              {action.badge && (
                <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm rounded-full px-2 py-1">
                  <span className="text-xs font-bold">{action.badge}</span>
                </div>
              )}
            </a>
          ))}
        </div>
      </div>

      {/* Stats Card Grid */}
      <MemoizedStatsCardGrid
        totalMembers={totalRegisteredMembers}
        activeNewcomers={newcomerStats.active}
        weeklyIncome={weeklyFinanceData.totalIncome || 0}
        weeklyExpense={weeklyFinanceData.totalExpense || 0}
        monthlyGrowth={Math.floor(Math.random() * 20) - 5}
        newcomerGrowth={Math.floor(Math.random() * 15) - 3}
        netIncome={weeklyFinanceData.netIncome || 0}
        profitMargin={typeof weeklyFinanceData.profitMargin === 'string' ? weeklyFinanceData.profitMargin : String(weeklyFinanceData.profitMargin || '0')}
        attendanceRate={85}
        activeEvents={12}
      />

      <div className="space-y-4 lg:space-y-6">
        {/* 실시간 활동 요약 */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">실시간 활동 요약</h2>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">실시간</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">{totalRegisteredMembers.toLocaleString()}</p>
              <p className="text-sm opacity-90">총 등록 교인</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">{newcomerStats.active}</p>
              <p className="text-sm opacity-90">활동 중인 새가족</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">{weeklyFinanceData.profitMargin}%</p>
              <p className="text-sm opacity-90">헌금증감률</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">85%</p>
              <p className="text-sm opacity-90">주간 출석률</p>
            </div>
          </div>
        </div>

        {/* 안내 문구 */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800 mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-200 text-center flex items-center justify-center">
            <FaLightbulb className="mr-2 h-4 w-4" />
            💡 각 영역의 상세 정보는 해당 버튼을 클릭해 주세요
          </p>
        </div>

        {/* 탭 네비게이션 - 재구성 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          {[
            { id: 'overview', label: '전체 현황', icon: <FaChartLine /> },
            { id: 'members', label: '성도 현황', icon: <FaUsers /> },
            { id: 'newcomers', label: '새가족 현황', icon: <FaChurch /> },
            { id: 'visitation', label: '심방 현황', icon: <FaHeartbeat /> },
            { id: 'discipleship', label: '양육 현황', icon: <FaBookOpen /> },
            { id: 'finance', label: '재정 현황', icon: <FaMoneyBillWave /> },
            { id: 'activities', label: '활동 현황', icon: <FaCalendarAlt /> },
            { id: 'analytics', label: '분석', icon: <FaChartBar /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-row items-center justify-center p-4 rounded-xl text-sm font-bold transition-all duration-200 shadow-sm border ${activeTab === tab.id
                  ? 'bg-blue-500 text-white border-blue-600 transform scale-[1.02] ring-2 ring-blue-300 dark:ring-blue-900'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-100 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-200'
                }`}
            >
              <div className={`mr-3 text-lg ${activeTab === tab.id ? 'text-white' : 'text-blue-500 dark:text-blue-400'}`}>
                {tab.icon}
              </div>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 콘텐츠 영역 */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* 전체 현황 섹션 */}
            <MemoizedOverallStatusSection
              totalMembers={totalRegisteredMembers}
              activeNewcomers={newcomerStats.active}
              weeklyIncome={weeklyFinanceData.totalIncome || 0}
              weeklyExpense={weeklyFinanceData.totalExpense || 0}
              netIncome={weeklyFinanceData.netIncome || 0}
              profitMargin={typeof weeklyFinanceData.profitMargin === 'string' ? weeklyFinanceData.profitMargin : String(weeklyFinanceData.profitMargin || '0')}
              attendanceRate={85}
              activeEvents={12}
            />

            {/* 실시간 활동 모니터링 */}
            <RealTimeActivityMonitor />

            {/* Pastoral Calendar Section */}
            <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg lg:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-3 lg:mb-4 hover:text-blue-600 transition-colors flex items-center">
                <FaCalendarAlt className="mr-2 text-blue-600" />
                목회 캘린더
              </h2>
              <div className="overflow-x-auto">
                <PastoralCalendar />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <MemoizedRegisteredMembersSection
              totalMembers={totalRegisteredMembers}
              ageGroupData={ageGroupData}
              genderData={genderData}
              memberClassData={memberClassData}
            />
          </div>
        )}

        {activeTab === 'newcomers' && (
          <MemoizedNewcomerStatusSection
            totalNewcomers={newcomerStats.total}
            activeNewcomers={newcomerStats.active}
            graduatedNewcomers={newcomerStats.graduated}
          />
        )}

        {activeTab === 'visitation' && (
          <MemoizedVisitationStatusSection
            totalVisitations={48}
            scheduledVisitations={12}
            completedVisitations={36}
            urgentCases={5}
          />
        )}

        {activeTab === 'discipleship' && (
          <MemoizedDiscipleshipStatusSection
            totalStudents={156}
            activePrograms={8}
            completedCourses={342}
            graduationRate={78}
          />
        )}

        {activeTab === 'finance' && (
          <MemoizedFinanceStatusSection
            totalIncome={mockFinanceData.totalIncome}
            totalExpense={mockFinanceData.totalExpense}
            netIncome={mockFinanceData.netIncome}
            profitMargin={mockFinanceData.profitMargin.toString()}
            monthlyBudget={2000000}
            budgetUsed={mockFinanceData.totalExpense}
          />
        )}

        {activeTab === 'activities' && (
          <div className="space-y-6">
            {/* 실시간 활동 모니터링 */}
            <RealTimeActivityMonitor />

            {/* 최근 활동 상세 */}
            <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg lg:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center">
                <FaClock className="mr-2 text-blue-600" />
                최근 활동 상세
              </h2>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.type === 'member' ? 'bg-blue-100 dark:bg-blue-900/30' :
                          activity.type === 'finance' ? 'bg-green-100 dark:bg-green-900/30' :
                            activity.type === 'discipleship' ? 'bg-purple-100 dark:bg-purple-900/30' :
                              activity.type === 'visitation' ? 'bg-orange-100 dark:bg-orange-900/30' :
                                'bg-yellow-100 dark:bg-yellow-900/30'
                        }`}>
                        {activity.type === 'member' && <FaUsers className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
                        {activity.type === 'finance' && <FaMoneyBillWave className="h-5 w-5 text-green-600 dark:text-green-400" />}
                        {activity.type === 'discipleship' && <FaUserFriends className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
                        {activity.type === 'visitation' && <FaHeartbeat className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
                        {activity.type === 'event' && <FaCalendarAlt className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">
                          <span className="font-semibold">{activity.user}</span> {activity.action}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{activity.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
                        {activity.type}
                      </span>
                      {activity.status === 'success' && <FaCheckCircle className="h-4 w-4 text-green-500" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* 상세 통계 섹션 */}
            <DetailedStatsSection />

            {/* 데이터 분석 카드 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-white mb-4 flex items-center">
                  <FaChartBar className="mr-2 text-blue-600" />
                  성장 추세 분석
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">월간 성장률</span>
                    <div className="flex items-center space-x-2">
                      <FaArrowUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">+12.5%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">새가족 유입률</span>
                    <div className="flex items-center space-x-2">
                      <FaArrowUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">+8.3%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">참여도 변화</span>
                    <div className="flex items-center space-x-2">
                      <FaArrowUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">+5.7%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-white mb-4 flex items-center">
                  <FaTrophy className="mr-2 text-yellow-600" />
                  성과 지표
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">목표 달성률</span>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">87%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">회원 만족도</span>
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">92%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">재정 건전성</span>
                    <span className="text-sm font-medium text-purple-600 dark:text-purple-400">95%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 인사이트 섹션 */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6 rounded-xl border border-indigo-200 dark:border-indigo-800">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-white mb-4 flex items-center">
                <FaLightbulb className="mr-2 text-yellow-600" />
                인사이트 및 권장사항
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">긍정적 지표</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• 새가족 유입이 꾸준히 증가하고 있습니다</li>
                    <li>• 재정 건전성이 매우 우수한 수준입니다</li>
                    <li>• 회원 참여도가 지속적으로 개선되고 있습니다</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">개선 제안</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• 50대 이상 교인 참여도를 높일 필요가 있습니다</li>
                    <li>• 온라인 프로그래을 확대해 보세요</li>
                    <li>• 새가족 적응 프로그램을 강화하세요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
